import { useState, useEffect } from '@wordpress/element';
import api from '../utils/api';

const { __ } = wp.i18n;

export default function Settings() {
    const [settings, setSettings] = useState({
        openai_api_key: '',
        google_api_key: '',
        grok_api_key: '',
        deepseek_api_key: '',
        default_post_status: 'publish',
        default_post_type: 'post',
        log_retention: 90,
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [notification, setNotification] = useState(null);
    const [validating, setValidating] = useState({});

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            const data = await api.getSettings();
            setSettings((prev) => ({ ...prev, ...data }));
        } catch (err) {
            setNotification({ message: err.message, type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setNotification(null);
        try {
            await api.saveSettings(settings);
            setNotification({
                message: AIBulkPost.i18n.saved,
                type: 'success',
            });
        } catch (err) {
            setNotification({ message: err.message, type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleValidate = async (provider) => {
        const keyField = `${provider}_api_key`;
        const apiKey = settings[keyField];

        if (!apiKey || apiKey.startsWith('***')) {
            setNotification({
                message: __('Please enter a new API key first.', 'ai-bulk-post'),
                type: 'error',
            });
            return;
        }

        setValidating((prev) => ({ ...prev, [provider]: true }));
        try {
            const result = await api.validateKey(provider, apiKey);
            if (result.valid) {
                const toSave = { ...settings };
                Object.keys(toSave).forEach((key) => {
                    if (typeof toSave[key] === 'string' && toSave[key].startsWith('***')) {
                        delete toSave[key];
                    }
                });
                await api.saveSettings(toSave);
                setNotification({
                    message: __('API key is valid and saved.', 'ai-bulk-post'),
                    type: 'success',
                });
            } else {
                setNotification({
                    message: __('API key is invalid.', 'ai-bulk-post'),
                    type: 'error',
                });
            }
        } catch (err) {
            setNotification({ message: err.message, type: 'error' });
        } finally {
            setValidating((prev) => ({ ...prev, [provider]: false }));
        }
    };

    const providers = [
        {
            id: 'openai',
            name: 'OpenAI',
            keyField: 'openai_api_key',
            url: 'https://platform.openai.com/api-keys',
        },
        {
            id: 'google',
            name: 'Google Gemini',
            keyField: 'google_api_key',
            url: 'https://aistudio.google.com/app/apikey',
        },
        {
            id: 'grok',
            name: 'Grok (xAI)',
            keyField: 'grok_api_key',
            url: 'https://console.x.ai',
        },
        {
            id: 'deepseek',
            name: 'DeepSeek',
            keyField: 'deepseek_api_key',
            url: 'https://platform.deepseek.com/api_keys',
        },
    ];

    if (loading) {
        return (
            <div className="aibp-loading">
                <span className="spinner is-active"></span>
                {__('Loading settings...', 'ai-bulk-post')}
            </div>
        );
    }

    return (
        <div className="aibp-settings">
            {notification && (
                <div className={`aibp-notice aibp-notice-${notification.type}`}>
                    {notification.message}
                </div>
            )}

            <h1>{__('Settings', 'ai-bulk-post')}</h1>

            {providers.map((provider) => (
                <div key={provider.id} className="aibp-settings-card">
                    <h2>{provider.name}</h2>
                    <div className="aibp-settings-field">
                        <label htmlFor={provider.keyField}>
                            {__('API Key', 'ai-bulk-post')}
                        </label>
                        <div className="aibp-settings-input-row">
                            <input
                                id={provider.keyField}
                                type="password"
                                value={settings[provider.keyField] || ''}
                                onChange={(e) =>
                                    setSettings((prev) => ({
                                        ...prev,
                                        [provider.keyField]: e.target.value,
                                    }))
                                }
                                className="regular-text"
                                placeholder={__('Enter API key', 'ai-bulk-post')}
                            />
                            <button
                                className="button"
                                onClick={() => handleValidate(provider.id)}
                                disabled={validating[provider.id]}
                            >
                                {validating[provider.id]
                                    ? __('Validating...', 'ai-bulk-post')
                                    : __('Verify', 'ai-bulk-post')}
                            </button>
                        </div>
                        <p className="description">
                            {__('Get your key at', 'ai-bulk-post')}{' '}
                            <a href={provider.url} target="_blank" rel="noopener noreferrer">
                                {provider.url}
                            </a>
                        </p>
                    </div>
                </div>
            ))}

            <div className="aibp-settings-card">
                <h2>{__('General', 'ai-bulk-post')}</h2>

                <div className="aibp-settings-field">
                    <label htmlFor="default_post_status">
                        {__('Default Post Status', 'ai-bulk-post')}
                    </label>
                    <select
                        id="default_post_status"
                        value={settings.default_post_status}
                        onChange={(e) =>
                            setSettings((prev) => ({
                                ...prev,
                                default_post_status: e.target.value,
                            }))
                        }
                    >
                        <option value="publish">{__('Publish', 'ai-bulk-post')}</option>
                        <option value="draft">{__('Draft', 'ai-bulk-post')}</option>
                        <option value="pending">{__('Pending Review', 'ai-bulk-post')}</option>
                    </select>
                </div>

                <div className="aibp-settings-field">
                    <label htmlFor="log_retention">
                        {__('Log Retention (days)', 'ai-bulk-post')}
                    </label>
                    <input
                        id="log_retention"
                        type="number"
                        min="7"
                        max="365"
                        value={settings.log_retention}
                        onChange={(e) =>
                            setSettings((prev) => ({
                                ...prev,
                                log_retention: parseInt(e.target.value, 10),
                            }))
                        }
                        className="small-text"
                    />
                </div>
            </div>

            <div className="aibp-settings-actions">
                <button
                    className="button button-primary"
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving
                        ? __('Saving...', 'ai-bulk-post')
                        : __('Save Settings', 'ai-bulk-post')}
                </button>
            </div>
        </div>
    );
}
